import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';

import { ControlError } from '../../models';

@Component({
  selector: 'kt-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  private subscriptions = new Subscription();
  private _control: FormControl;
  filteredOptions$: Observable<any[]>;
  searchControl = new FormControl();
  required: boolean;
  removable: boolean;
  showClearBtn: boolean;
  @ViewChild('searchInput') searchInput: ElementRef;
  @Input() isChipList: boolean;
  // can be set to true only for primitive option type.
  @Input() allowUserInput: boolean;
  @Input() id: string;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() color: 'primary' | 'secondary' | 'tertiary' | 'neutral' = 'neutral';
  // can not be used with a chip list.
  @Input() optionsVisible: boolean;
  @Input() optionsType: 'LIST' | 'API';
  @Input() options: any[];
  @Input() ignoredOptions = [];
  @Input() optionsAPI: (term: string) => Observable<any>;
  @Input() optionType: 'PRIMITIVE' | 'OBJECT';
  @Input() objectOptionReq: {
    idKey: any;
    searchKey?: string;
    userInputKey?: string;
    dataPath?: string;
    labelFormatter?: (option: any) => string;
    valueFormatter?: (option: any) => string;
  };
  @Input() errors: ControlError[] = [];
  @Input()
  set control(control: FormControl) {
    if (!control) return;

    this._control = control;
    this.showClearBtn = !this.isChipList && !!control.value;

    this.required = false;
    this.removable = true;
    this.searchControl.enable();

    if (control.validator) {
      const validator = control.validator({} as any);
      this.required = !!validator?.required;
    }

    if (control.disabled) {
      this.removable = false;
      this.searchControl.disable();
    }

    if (!this.isChipList) {
      this.searchControl.setValidators(() => control.errors);
    }
  }
  get control(): FormControl {
    return this._control;
  }
  @Output() focused = new EventEmitter();

  ngOnInit(): void {
    this.subscriptions.add(
      this.control.valueChanges.subscribe((value) => {
        this.showClearBtn = !this.isChipList && !!value;

        if (!value && this.control.untouched && this.control.pristine) {
          this.searchControl.reset('');
        } else {
          this.searchControl.setValue(
            this.isChipList
              ? ''
              : value
              ? this.optionType === 'OBJECT' && this.objectOptionReq?.valueFormatter
                ? this.objectOptionReq.valueFormatter(value)
                : value
              : '',
          );
        }

        if (this.isChipList) {
          this.searchInput.nativeElement.value = '';
        } else {
          this.searchControl.setValidators(() => this.control.errors);

          if (this.control.touched || this.control.dirty) {
            this.searchControl.markAsTouched();
            this.searchControl.updateValueAndValidity();
          }
        }

        if (this.control.disabled) {
          this.removable = false;
          this.searchControl.disable();
        } else {
          this.removable = true;
          this.searchControl.enable();
        }
      }),
    );

    if (!this.isChipList && this.control.value) {
      this.searchControl.setValue(
        this.optionType === 'OBJECT' && this.objectOptionReq?.valueFormatter
          ? this.objectOptionReq.valueFormatter(this.control.value)
          : this.control.value,
      );
    }

    if (this.optionsType) {
      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(500),
        distinctUntilChanged(),
        this.optionsType === 'LIST'
          ? map((term) => this.filter(typeof term === 'object' ? '' : term?.toLowerCase().trim()))
          : switchMap((term) =>
              this.optionsAPI
                ? this.optionsAPI(typeof term === 'object' ? '' : term?.toLowerCase().trim()).pipe(
                    map((res) => {
                      let options = this.objectOptionReq?.dataPath
                        ? res[this.objectOptionReq?.dataPath]
                        : res;
                      if (this.ignoredOptions.length) {
                        options = options.filter(
                          (o) =>
                            !this.ignoredOptions.find((i) =>
                              this.optionType === 'PRIMITIVE'
                                ? i === o
                                : i[this.objectOptionReq.idKey] === o[this.objectOptionReq.idKey],
                            ),
                        );
                      }
                      return options;
                    }),
                  )
                : of([]),
            ),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private filter(term: string): any[] {
    let options = this.options?.filter((option) => {
      return this.optionType === 'OBJECT' && this.objectOptionReq?.searchKey
        ? option[this.objectOptionReq.searchKey]?.toString().toLowerCase().includes(term)
        : option?.toString().toLowerCase().includes(term);
    });
    if (this.ignoredOptions.length) {
      options = options.filter(
        (o) =>
          !this.ignoredOptions.find((i) =>
            this.optionType === 'PRIMITIVE'
              ? i === o
              : i[this.objectOptionReq.idKey] === o[this.objectOptionReq.idKey],
          ),
      );
    }
    return options;
  }

  add(item: any, isUserinput?: boolean): void {
    item =
      isUserinput && this.optionType === 'OBJECT'
        ? { [this.objectOptionReq.userInputKey]: item }
        : item;
    this.control.markAsTouched();
    this.control.setValue(this.isChipList ? [...(this.control.value || []), item] : item);
  }

  remove(item: any): void {
    const controlVal = this.control.value;

    this.control.markAsTouched();

    if (
      controlVal?.length === 1 &&
      (this.optionType === 'OBJECT'
        ? controlVal[0][this.objectOptionReq.idKey] === item[this.objectOptionReq.idKey]
        : controlVal[0] === item)
    ) {
      this.control.setValue(null);
    } else {
      this.control.setValue(
        controlVal?.filter((i) =>
          this.optionType === 'OBJECT'
            ? i[this.objectOptionReq.idKey] !== item[this.objectOptionReq.idKey]
            : i !== item,
        ),
      );
    }
  }

  isSelected(option: any): boolean {
    let isSelected: boolean;
    const controlVal = this.control.value;

    if (this.isChipList) {
      isSelected = !!controlVal?.find((i) =>
        this.optionType === 'OBJECT'
          ? i[this.objectOptionReq.idKey] === option[this.objectOptionReq.idKey]
          : i === option,
      );
    } else {
      isSelected =
        this.optionType === 'OBJECT'
          ? !!controlVal &&
            controlVal[this.objectOptionReq.idKey] === option[this.objectOptionReq.idKey]
          : controlVal === option;
    }

    return isSelected;
  }

  clearSearch(): void {
    this.control.markAsTouched();

    if (!this.isChipList && this.control.value) return;

    this.searchControl.setValue('');

    if (this.isChipList) {
      this.searchInput.nativeElement.value = '';
    }
  }

  clearControl(): void {
    this.control.markAsTouched();
    this.control.setValue(null);
  }

  focus(): void {
    this.searchInput.nativeElement.focus();
  }

  blur(): void {
    this.searchInput.nativeElement.blur();
  }
}
